import clsx from 'clsx';
import { type ReactNode, type ReactElement } from 'react';
import { isElement } from 'react-is';
import type {
  GroupBase,
  PlaceholderProps,
  ValueContainerProps as ReactSelectValueContainerProps,
} from 'react-select';
import { components } from 'react-select';

import { _canReceiveDefaultTextFormat } from '@dynatrace/strato-components/core';

import { Placeholder } from './Placeholder.js';
import { selectValueContainerTruncationBoxCSS } from '../styles/Select.sty.js';
import type { BaseOption } from '../types.js';

export type ValueContainerProps<
  OptionValue,
  isMulti extends boolean,
  Group extends GroupBase<OptionValue>,
> = ReactSelectValueContainerProps<OptionValue, isMulti, Group>;

/**
 * Typeguard that will check if a ReactElement is a `ValueContainer`
 * @internal
 */
export function _isValueContainerComponent<
  OptionValue,
  isMulti extends boolean,
  Group extends GroupBase<OptionValue>,
>(
  element: ReactNode,
): element is ReactElement<ValueContainerProps<OptionValue, isMulti, Group>> {
  if (isElement(element) && element.type === ValueContainer) {
    return true;
  }
  return false;
}

export function ValueContainer<
  OptionValue,
  isMulti extends boolean,
  Group extends GroupBase<OptionValue>,
>(props: ValueContainerProps<OptionValue, isMulti, Group>) {
  const { selectProps, children } = props;

  // display custom trigger value
  if (
    selectProps.value !== undefined &&
    selectProps.value !== null &&
    selectProps.displayValue
  ) {
    if (_canReceiveDefaultTextFormat(selectProps.displayValue)) {
      return (
        <components.ValueContainer {...props}>
          <span className={clsx(selectValueContainerTruncationBoxCSS)}>
            {selectProps.displayValue}
          </span>
        </components.ValueContainer>
      );
    }

    return (
      <components.ValueContainer {...props}>
        {selectProps.displayValue}
      </components.ValueContainer>
    );
  }

  // This checks if anything is defined in the react-select children props that would be
  // rendered in the trigger, i.e. Placeholder, SingleValueContainer, MultiValueContainer
  if (
    Array.isArray(children) &&
    (children?.[0] === null || children?.[0] === undefined)
  ) {
    if (props.hasValue) {
      const displayValue = props.getValue();

      return (
        <components.ValueContainer {...props}>
          {/* eslint-disable-next-line @typescript-eslint/prefer-ts-expect-error, @typescript-eslint/ban-ts-comment */}
          {/* @ts-ignore Strict mode transition - needs to be investigated */}
          {(displayValue[0] as BaseOption<OptionValue>)?.label?.[0]}
        </components.ValueContainer>
      );
    } else {
      return (
        <components.ValueContainer {...props}>
          <span className={clsx(selectValueContainerTruncationBoxCSS)}>
            <Placeholder
              {...(props as PlaceholderProps<OptionValue, isMulti, Group>)}
            />
          </span>
        </components.ValueContainer>
      );
    }
  }

  return (
    <components.ValueContainer {...props}>
      {/* The elements are reordered here, as the selected options inside the trigger
      would otherwise scroll to the left when the hidden input is focused */}
      <span className={clsx(selectValueContainerTruncationBoxCSS)}>
        {Array.isArray(children) && children[0]}
      </span>
    </components.ValueContainer>
  );
}
